import { useEffect, useRef, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { DURATION, EASE_OUT_IOS, useReducedMotion } from '../../lib/motion';

/**
 * Cross-fade + micro-slide for the body under a {@link SegmentedControl}. The pill
 * physically slides between segments; without this the *content* beneath hard-cuts,
 * so a gliding pill sits on top of an instant swap — that mismatch is what reads as
 * jank on the most-used surfaces (league hub tabs, standings views).
 *
 * Enter motion runs only when the active segment *changes* after mount — not on the
 * first paint (e.g. arriving from a bottom tab), where it would stack with a page
 * transition and read as a sideways bounce.
 *
 * Honours Reduce Motion (renders the body with no movement).
 */
export function SegmentedViews<T extends string>({
  value,
  order,
  children,
  className
}: {
  value: T;
  order: readonly T[];
  children: ReactNode;
  className?: string;
}) {
  const reduce = useReducedMotion();
  // Previous *committed* value drives the enter direction. Updated in an effect (not
  // during render) so it survives StrictMode's double-invoke and reads correctly on
  // the render where `value` flips.
  const isMountRef = useRef(true);
  const prevRef = useRef(value);
  const dir = order.indexOf(value) >= order.indexOf(prevRef.current) ? 1 : -1;
  useEffect(() => {
    isMountRef.current = false;
    prevRef.current = value;
  }, [value]);

  // Enter motion is for in-screen segment changes only. On first mount (e.g. arriving
  // from a bottom tab) it stacked with NavStack's page fade and read as a sideways bounce.
  if (reduce || isMountRef.current) return <div className={className}>{children}</div>;

  return (
    <motion.div
      key={value}
      className={className}
      initial={{ opacity: 0, x: dir * 12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: DURATION.small / 1000, ease: EASE_OUT_IOS }}
    >
      {children}
    </motion.div>
  );
}
